import { useEffect } from "react";

const ORIGIN = "https://highergrounddc.com";
const DEFAULT_TITLE = "Higher Ground DC Cannabis Dispensary | Washington DC Near Union Market";
const DEFAULT_DESC =
  "Higher Ground DC Cannabis Dispensary is a premium Washington DC cannabis destination near Union Market with premium flower, vapes, concentrates, edibles, and expert patient-first service.";

const setMeta = (selector, attr, value) => {
  const el = document.querySelector(selector);
  const prev = el ? el.getAttribute(attr) : null;
  if (el && value != null) el.setAttribute(attr, value);
  return { el, prev };
};

// Per-page SEO metadata. Keyed by route path.
export const SEO_PAGES = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESC,
    breadcrumb: [["Home", "/"]],
  },
  "/shop": {
    title: "Shop Cannabis in Washington DC | Flower, Vapes & Edibles — Higher Ground DC",
    description:
      "Shop premium cannabis at Higher Ground DC near Union Market: flower, prerolls, vapes, cartridges, concentrates, edibles, and tinctures. View the live menu with real-time pricing and potency.",
    breadcrumb: [["Home", "/"], ["Shop", "/shop"]],
  },
  "/about": {
    title: "About Higher Ground DC | Patient-First Cannabis Dispensary in Washington DC",
    description:
      "Higher Ground DC is a patient-first medical cannabis dispensary and lounge in Northeast Washington DC, near Union Market. Learn about our philosophy, space, and the Higher Ground difference.",
    breadcrumb: [["Home", "/"], ["About", "/about"]],
  },
  "/events": {
    title: "Cannabis Events in Washington DC | Yoga, Wellness & Community — Higher Ground DC",
    description:
      "See what's on at Higher Ground DC — 420-friendly yoga, sound baths, candle-making, community walks, and networking nights at our Washington DC cannabis lounge near Union Market. 21+.",
    breadcrumb: [["Home", "/"], ["Events", "/events"]],
  },
  "/areas-served": {
    title: "Areas Served | Cannabis Dispensary Serving Washington DC & Nearby — Higher Ground DC",
    description:
      "Higher Ground DC serves Washington DC neighborhoods including NoMa, Union Market, Ivy City, Capitol Hill, Brookland, and nearby Maryland communities from 1135 Okie St NE.",
    breadcrumb: [["Home", "/"], ["Areas Served", "/areas-served"]],
  },
  "/blog": {
    title: "Cannabis Blog & DC Guides | Higher Ground DC",
    description:
      "Cannabis education, product guides, and Washington DC city guides from Higher Ground DC — including how to get a DC medical card, dispensary tips, and community events.",
    breadcrumb: [["Home", "/"], ["Blog", "/blog"]],
  },
  "/media": {
    title: "Media Gallery | Higher Ground DC Cannabis Dispensary & Lounge",
    description:
      "Photos of Higher Ground DC — our Washington DC cannabis dispensary and lounge near Union Market, community events, wellness sessions, and the space itself.",
    breadcrumb: [["Home", "/"], ["Media", "/media"]],
  },
  "/contact": {
    title: "Contact & Visit | Higher Ground DC — 1135 Okie St NE, Washington DC",
    description:
      "Visit Higher Ground DC at 1135 Okie St NE, Washington, DC 20002, near Union Market. Hours: Mon–Sat 12–8 PM, Sun 12–7 PM. Call (202) 800-1135 or send us a message.",
    breadcrumb: [["Home", "/"], ["Contact", "/contact"]],
  },
  "/visit": {
    title: "First Visit Pass | Higher Ground DC Cannabis Lounge in Washington DC",
    description:
      "New to Higher Ground DC? Claim your First Visit Pass — a free joint with your first $30+ purchase at our Washington DC cannabis dispensary and lounge near Union Market. 21+.",
    breadcrumb: [["Home", "/"], ["Visit", "/visit"]],
  },
  "/signup": {
    title: "Get Deals & Event Updates | Higher Ground DC — Washington DC",
    description:
      "Join the Higher Ground DC email list for weekly deals, event announcements, and community news from Washington DC's premium cannabis dispensary and lounge near Union Market.",
    breadcrumb: [["Home", "/"], ["Sign Up", "/signup"]],
  },
  "/waiver": {
    title: "Safe Use Waiver | Higher Ground DC Cannabis Lounge — Washington DC",
    description:
      "Complete the Higher Ground DC Safe Use Treatment Facility and Summer Garden waiver before visiting our Washington DC cannabis lounge near Union Market. 21+ with valid ID.",
    breadcrumb: [["Home", "/"], ["Waiver", "/waiver"]],
  },
};

/**
 * Lightweight per-page SEO. Sets title, meta description, canonical, OG/Twitter,
 * and injects a BreadcrumbList + WebPage JSON-LD graph. Restores previous values on unmount.
 * No external dependency; safe for the SPA and picked up by JS-rendering crawlers.
 * Pass `custom` ({ title, description, breadcrumb }) for dynamic routes like /dispensary/:slug.
 */
export const useSeo = (path, custom) => {
  useEffect(() => {
    const cfg = custom || SEO_PAGES[path] || {};
    const title = cfg.title || DEFAULT_TITLE;
    const description = cfg.description || DEFAULT_DESC;
    const canonical = `${ORIGIN}${path === "/" ? "/" : path}`;

    const prevTitle = document.title;
    document.title = title;

    const restores = [];
    restores.push(setMeta('meta[name="description"]', "content", description));
    restores.push(setMeta('link[rel="canonical"]', "href", canonical));
    restores.push(setMeta('meta[property="og:title"]', "content", title));
    restores.push(setMeta('meta[property="og:description"]', "content", description));
    restores.push(setMeta('meta[property="og:url"]', "content", canonical));
    restores.push(setMeta('meta[name="twitter:title"]', "content", title));
    restores.push(setMeta('meta[name="twitter:description"]', "content", description));

    const graph = [
      {
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: title,
        description,
        isPartOf: { "@id": `${ORIGIN}/#website` },
        about: { "@id": `${ORIGIN}/#store` },
      },
    ];
    if (cfg.breadcrumb) {
      graph.push({
        "@type": "BreadcrumbList",
        itemListElement: cfg.breadcrumb.map(([name, url], i) => ({
          "@type": "ListItem",
          position: i + 1,
          name,
          item: `${ORIGIN}${url}`,
        })),
      });
    }
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-page-ld", "1");
    ld.text = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      restores.forEach(({ el, prev }) => {
        if (el && prev !== null) el.setAttribute(el.tagName === "LINK" ? "href" : "content", prev);
      });
      ld.remove();
    };
  }, [path, custom?.title, custom?.description]);
};
